import { useSearchParams } from 'react-router';
import { Tabs } from '@cloudscape-design/components';
import { EmptyState, PageLayout, useRoles } from '@deloitte-us-consulting/gp-sdk';
import DataAccessAdmin from './DataAccessAdmin';
import DataSourcesAdmin from './DataSourcesAdmin';
import SystemsPanel from './data/SystemsPanel';

const TABS = ['systems', 'apps', 'assistant'] as const;
type TabId = (typeof TABS)[number];

/**
 * The single Data page for platform admins (ADR 0031, "Data systems"):
 *   Systems    the external systems (AirTable, WorkSpan, ...); open one for its tables and fields
 *   Apps       which systems each app reads, grants, and the request ledger
 *   Assistant  what the platform assistant can read (separate from app access)
 * The tab and system live in the URL (`?tab=apps`, `?tab=systems&system=airtable`) so
 * a view can be linked to. Gated to platform admins; the API enforces the same gate.
 */
export default function DataAdmin() {
  const { can } = useRoles();
  const isAllowed = can('Data:Manage', { appId: '*' });
  const [params, setParams] = useSearchParams();

  if (!isAllowed) {
    return (
      <PageLayout title="Data">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </PageLayout>
    );
  }

  const requested = params.get('tab');
  const tab: TabId = (TABS as readonly string[]).includes(requested ?? '') ? (requested as TabId) : 'systems';
  const system = params.get('system') ?? undefined;

  return (
    <PageLayout
      title="Data"
      description="The systems the platform connects to, which apps read from them, and what the assistant can see. Changes apply on the next request."
    >
      <Tabs
        activeTabId={tab}
        onChange={({ detail }) => setParams({ tab: detail.activeTabId })}
        tabs={[
          {
            id: 'systems',
            label: 'Systems',
            content: (
              <SystemsPanel
                systemId={tab === 'systems' ? system : undefined}
                onOpen={(id) => setParams({ tab: 'systems', system: id })}
                onBack={() => setParams({ tab: 'systems' })}
              />
            ),
          },
          { id: 'apps', label: 'Apps', content: <DataAccessAdmin embedded /> },
          { id: 'assistant', label: 'Assistant', content: <DataSourcesAdmin embedded /> },
        ]}
      />
    </PageLayout>
  );
}
